import { AudioLines, Loader2 } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  resolveTranscriptionLanguage,
  TranscriptionLanguageSelect,
} from "@/components/ui/transcription-language-select";
import { transcribeAudioClip } from "@/lib/api/transcription";
import { log } from "@/lib/logger/browser-logger";
import { cn } from "@/lib/utils";

export type VoiceInputState = "idle" | "recording" | "transcribing";

type VoiceInputButtonProps = {
  /** Called with the trimmed transcript once the recording is transcribed. */
  onTranscript: (text: string) => void;
  /**
   * ISO-639-1 code the dropdown defaults to (use appLanguageToTranscriptionCode
   * on the flow language when in a flow, else the user language). The advisor
   * can override it per-recording via the dropdown.
   */
  defaultLanguage?: string;
  /**
   * Controlled language. Pass with onLanguageChange to render the picker
   * yourself (TranscriptionLanguageSelect) and set hideLanguagePicker.
   */
  language?: string;
  onLanguageChange?: (language: string) => void;
  hideLanguagePicker?: boolean;
  /** Reports idle/recording/transcribing, e.g. to lock an external picker. */
  onStateChange?: (state: VoiceInputState) => void;
  /** "sm" = 24px controls (editor toolbars), "md" = 32px (chat composer). */
  size?: "sm" | "md";
  labels: {
    start: string;
    stop: string;
    transcribing: string;
    error: string;
    micDenied: string;
    language: string;
  };
};

/**
 * Map the app's locale (e.g. "nb_NO", "en_GB") to the Whisper language code the
 * transcriber expects. Norwegian Bokmål (nb) → "no", Nynorsk (nn) → "nn".
 * Returns undefined for unknown locales so the picker falls back to its default.
 */
export function appLanguageToTranscriptionCode(appLanguage: string): string | undefined {
  const prefix = appLanguage.slice(0, 2).toLowerCase();
  const map: Record<string, string> = {
    nb: "no",
    nn: "nn",
    no: "no",
    sv: "sv",
    da: "da",
    fi: "fi",
    is: "is",
    en: "en",
    de: "de",
    es: "es",
    fr: "fr",
    it: "it",
    nl: "nl",
    pt: "pt",
    hi: "hi",
    ja: "ja",
    zh: "zh",
  };
  return map[prefix];
}

/** First MediaRecorder mime the browser supports (Chrome/FF → webm, Safari → mp4). */
function pickMimeType(): string {
  if (typeof MediaRecorder === "undefined") return "";
  const candidates = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/mpeg"];
  for (const candidate of candidates) {
    if (MediaRecorder.isTypeSupported(candidate)) return candidate;
  }
  return "";
}

/**
 * Batch voice-to-text button: record in the browser via MediaRecorder, then
 * transcribe server-side (Scaleway Whisper). Shows an inline recording indicator
 * (timer + live level meter) while capturing, and a spinner while transcribing.
 */
export function VoiceInputButton({
  onTranscript,
  defaultLanguage,
  language,
  onLanguageChange,
  hideLanguagePicker,
  onStateChange,
  size = "sm",
  labels,
}: VoiceInputButtonProps) {
  const [state, setState] = useState<VoiceInputState>("idle");
  const [seconds, setSeconds] = useState(0);
  const [level, setLevel] = useState(0); // 0..1 RMS for the meter
  // Selected language for the transcriber; defaults to flow/user language,
  // falling back to the Nordic default for unknown locales. Controlled when
  // the parent passes `language`.
  const [ownLang, setOwnLang] = useState<string>(() =>
    resolveTranscriptionLanguage(defaultLanguage),
  );
  const selectedLang = language ?? ownLang;
  const setSelectedLang = onLanguageChange ?? setOwnLang;

  useEffect(() => {
    onStateChange?.(state);
  }, [state, onStateChange]);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const rafRef = useRef<number | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const cleanup = useCallback(() => {
    if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = null;
    if (audioCtxRef.current) {
      audioCtxRef.current.close().catch(() => {});
      audioCtxRef.current = null;
    }
    if (streamRef.current) {
      for (const track of streamRef.current.getTracks()) track.stop();
      streamRef.current = null;
    }
    setLevel(0);
    setSeconds(0);
  }, []);

  // Tear down capture if the editor unmounts mid-recording.
  useEffect(() => cleanup, [cleanup]);

  const startMeter = useCallback((stream: MediaStream) => {
    const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioCtx) return;
    const ctx: AudioContext = new AudioCtx();
    audioCtxRef.current = ctx;
    const source = ctx.createMediaStreamSource(stream);
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 512;
    source.connect(analyser);
    const data = new Uint8Array(analyser.frequencyBinCount);
    const tick = () => {
      analyser.getByteTimeDomainData(data);
      let sum = 0;
      for (const v of data) {
        const n = (v - 128) / 128;
        sum += n * n;
      }
      setLevel(Math.min(1, Math.sqrt(sum / data.length) * 3));
      rafRef.current = requestAnimationFrame(tick);
    };
    tick();
  }, []);

  const transcribe = useCallback(
    async (blob: Blob, mimeType: string) => {
      setState("transcribing");
      try {
        const ext = mimeType.includes("mp4") ? "mp4" : mimeType.includes("mpeg") ? "mp3" : "webm";
        const { text } = await transcribeAudioClip(blob, `recording.${ext}`, selectedLang);
        if (text.trim()) onTranscript(text.trim());
      } catch (err) {
        log.error({ err }, "voice transcription request failed");
        alert(labels.error);
      } finally {
        setState("idle");
      }
    },
    [onTranscript, labels.error, selectedLang],
  );

  const startRecording = useCallback(async () => {
    const mimeType = pickMimeType();
    if (!mimeType) {
      alert(labels.error);
      return;
    }
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (err) {
      log.error({ err }, "microphone access denied");
      alert(labels.micDenied);
      return;
    }
    streamRef.current = stream;
    chunksRef.current = [];

    const recorder = new MediaRecorder(stream, { mimeType });
    mediaRecorderRef.current = recorder;
    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunksRef.current.push(e.data);
    };
    recorder.onstop = () => {
      // recorder.mimeType is the type actually recorded (may differ from the
      // requested one, e.g. a video/* container for audio-only on Chrome).
      const actualType = recorder.mimeType || mimeType;
      const blob = new Blob(chunksRef.current, { type: actualType });
      cleanup();
      void transcribe(blob, actualType);
    };
    recorder.start();
    setState("recording");
    setSeconds(0);
    timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
    startMeter(stream);
  }, [labels.error, labels.micDenied, cleanup, transcribe, startMeter]);

  const stopRecording = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== "inactive") {
      recorder.stop(); // fires onstop → transcribe
    }
  }, []);

  const mm = String(Math.floor(seconds / 60)).padStart(2, "0");
  const ss = String(seconds % 60).padStart(2, "0");

  const md = size === "md";

  return (
    <div className="flex items-center gap-1 shrink-0">
      {/* Language hint for the transcriber — locked while recording/transcribing. */}
      {!hideLanguagePicker && (
        <TranscriptionLanguageSelect
          value={selectedLang}
          onChange={setSelectedLang}
          disabled={state !== "idle"}
          label={labels.language}
          size={size}
        />
      )}

      {state === "transcribing" && (
        <span
          className={cn(
            "flex items-center font-medium text-muted-foreground",
            md ? "h-8 gap-1.5 rounded-md bg-muted px-2.5 text-xs" : "h-6 gap-1 px-1.5 text-[10px]",
          )}
        >
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          {labels.transcribing}
        </span>
      )}

      {state === "recording" && (
        <div
          className={cn(
            "flex items-center rounded-md bg-destructive/10",
            md ? "h-8 gap-2 pl-2.5 pr-1" : "h-6 gap-1.5 pl-1.5 pr-0.5",
          )}
        >
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-destructive opacity-75" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-destructive" />
          </span>
          <span
            className={cn(
              "font-mono tabular-nums text-destructive",
              md ? "text-xs" : "text-[10px]",
            )}
          >{`${mm}:${ss}`}</span>
          <span
            className={cn(
              "flex items-end overflow-hidden rounded-sm bg-destructive/20",
              md ? "h-3.5 w-6" : "h-2.5 w-6",
            )}
            aria-hidden
          >
            <span
              className="w-full bg-destructive transition-[height] duration-75"
              style={{ height: `${Math.round(level * 100)}%` }}
            />
          </span>
          <Button
            variant="ghost"
            size="icon"
            onClick={stopRecording}
            className={cn("p-0", md ? "h-6 w-6 bg-destructive hover:bg-destructive/90" : "h-5 w-5")}
            type="button"
            tooltip={labels.stop}
          >
            {/* A box, not the Square icon: Button forces every svg to 16px. */}
            <span
              className={cn(
                "h-2 w-2 rounded-[1px]",
                md ? "bg-destructive-foreground" : "bg-destructive",
              )}
            />
          </Button>
        </div>
      )}

      {state === "idle" && (
        <Button
          tooltip={labels.start}
          variant="ghost"
          size="icon"
          onClick={startRecording}
          className={md ? "h-8 w-8 text-muted-foreground hover:text-foreground" : "h-6 w-6"}
          type="button"
        >
          <AudioLines className={cn("shrink-0", md ? "h-4 w-4" : "h-3.5 w-3.5")} />
        </Button>
      )}
    </div>
  );
}
